import React from 'react'
import { useTranslation } from 'react-i18next'

import { Button, ButtonVariant, ShapeVariant } from '@makyoapp/makyo-ui'

import { ReactComponent as DownloadIcon } from '@/assets/vectors/icon_lucide_download.svg?react'
import { ReactComponent as PrinterIcon } from '@/assets/vectors/icon_lucide_printer.svg?react'
import { ReactComponent as XIcon } from '@/assets/vectors/icon_lucide_x.svg?react'

interface BillingInvoiceProps {
  invoice: any // TODO: Add proper type
  onClose: () => void
  formatDate: (date: string) => string
}

export const BillingInvoice: React.FC<BillingInvoiceProps> = ({ invoice, onClose, formatDate }) => {
  const { t } = useTranslation()

  // Print invoice
  const handlePrint = () => {
    // Create a new window for printing
    const printWindow = window.open('', '_blank')
    if (!printWindow) return

    // Add print-specific styles
    const printStyles = `
      @media print {
        body {
          padding: 20px;
          margin: 0;
          color: #000;
          background: #fff;
        }
        .print-content {
          width: 100%;
          max-width: 100%;
          margin: 0;
          padding: 20px;
          background: #fff;
        }
        table {
          page-break-inside: auto;
        }
        tr {
          page-break-inside: avoid;
          page-break-after: auto;
        }
        thead {
          display: table-header-group;
        }
        tfoot {
          display: table-footer-group;
        }
      }
    `

    // Get all stylesheets from the current document
    const styleSheets = Array.from(document.styleSheets)
    let styles = ''

    // Extract styles from all stylesheets
    styleSheets.forEach((sheet) => {
      try {
        const cssRules = Array.from(sheet.cssRules)
        cssRules.forEach((rule) => {
          styles += rule.cssText + '\n'
        })
      } catch (e) {
        // Skip external stylesheets that might cause CORS issues
        console.warn('Could not load styles from stylesheet', e)
      }
    })

    // Get the content to print
    const content = document.querySelector('.invoice-content')?.cloneNode(true) as HTMLElement
    if (!content) return

    // Write the content to the new window
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Invoice #${invoice.invoiceNumber}</title>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          
          <!-- Include Tailwind CSS -->
          <script src="https://cdn.tailwindcss.com"></script>
          
          <!-- Custom styles -->
          <style>
            ${styles}
            ${printStyles}
            
            /* Additional styles to match modal appearance */
            .print-content {
              font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
            }
            
            /* Override dark mode in print */
            @media print {
              .dark\\:bg-gray-800 { background-color: white !important; }
              .dark\\:text-white { color: black !important; }
              .dark\\:text-gray-300 { color: #4B5563 !important; }
              .dark\\:text-gray-400 { color: #6B7280 !important; }
              .dark\\:border-gray-700 { border-color: #E5E7EB !important; }
              .dark\\:divide-gray-700 { border-color: #E5E7EB !important; }
              
              /* Ensure good contrast for printing */
              * {
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
              }
            }
          </style>
        </head>
        <body class="bg-white">
          <div class="print-content">
            ${content.outerHTML}
          </div>
        </body>
      </html>
    `)

    printWindow.document.close()

    // Wait for content and styles to load before printing
    printWindow.onload = () => {
      // Give extra time for Tailwind to initialize
      setTimeout(() => {
        printWindow.print()
        // Close the print window after printing
        setTimeout(() => {
          printWindow.close()
        }, 500)
      }, 500)
    }
  }

  // Save invoice
  const handleSave = () => {
    alert(t('Invoice download started!'))
  }

  return (
    <div className='fixed inset-0 z-99999 overflow-y-auto'>
      <div className='flex items-center justify-center min-h-screen p-4'>
        <div className='fixed inset-0 bg-black bg-opacity-50 transition-opacity print:hidden' onClick={onClose}></div>

        <div className='relative rounded-lg shadow-xl overflow-hidden max-w-4xl w-full p-6 bg-white dark:bg-gray-800 text-gray-800 dark:text-white'>
          <div className='absolute top-4 left-4 flex space-x-2 print:hidden'>
            <Button
              onClick={handlePrint}
              className='px-2.5'
              noPadding
              variant={ButtonVariant.Gray}
              shape={ShapeVariant.Circle}
              icon={<PrinterIcon className='w-4 h-4 fill-transparent' />}
            />
            <Button
              onClick={handleSave}
              className='px-2.5'
              icon={<DownloadIcon className='w-4 h-4 fill-transparent' />}
              noPadding
              variant={ButtonVariant.Gray}
              shape={ShapeVariant.Circle}
            />
          </div>
          <div className='absolute top-4 right-4 print:hidden'>
            <Button
              onClick={onClose}
              className='px-2.5'
              icon={<XIcon className='w-4 h-4 fill-transparent' />}
              noPadding
              variant={ButtonVariant.Gray}
              shape={ShapeVariant.Circle}
            />
          </div>

          <div className='invoice-content'>
            <div className='mb-5 text-center'>
              <h2 className='text-3xl font-bold'>Makyo</h2>
              <p className='text-gray-600 dark:text-gray-300'>
                {t('Invoice')} #{invoice.invoiceNumber}
              </p>
              <p className='text-sm mt-2 text-gray-500 dark:text-gray-400'>
                {t('Subscription Period')}: {formatDate(invoice.date)} -{' '}
                {formatDate(
                  new Date(new Date(invoice.date).setMonth(new Date(invoice.date).getMonth() + 1)).toISOString()
                )}
              </p>
            </div>

            <div className='grid grid-cols-1 md:grid-cols-2 gap-8 mb-8'>
              <div>
                <h3 className='text-lg font-semibold mb-2'>{t('Bill To')}</h3>
                <p className='text-gray-600 dark:text-gray-300'>
                  {t('Your Company Name')}
                  <br />
                  {t('123 Main St, San Francisco, CA 94105')}
                </p>
              </div>

              <div className='text-right'>
                <div className='mb-4'>
                  <h3 className='text-lg font-semibold mb-1'>{t('Invoice Date')}</h3>
                  <p className='text-gray-600 dark:text-gray-300'>{formatDate(invoice.date)}</p>
                </div>

                <div>
                  <h3 className='text-lg font-semibold mb-1'>{t('Payment Method')}</h3>
                  <p className='text-gray-600 dark:text-gray-300'>
                    {t('Credit Card')} •••• {invoice.cardLast4}
                  </p>
                </div>
              </div>
            </div>

            <div className={`overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700 mb-8`}>
              <table className='min-w-full divide-y divide-gray-200 dark:divide-gray-700'>
                <thead className='bg-gray-50 dark:bg-gray-700'>
                  <tr>
                    <th scope='col' className='px-6 py-3 text-left text-xs font-medium uppercase tracking-wider'>
                      {t('Item')}
                    </th>
                    <th scope='col' className='px-6 py-3 text-right text-xs font-medium uppercase tracking-wider'>
                      {t('Price')}
                    </th>
                    <th scope='col' className='px-6 py-3 text-right text-xs font-medium uppercase tracking-wider'>
                      {t('Quantity')}
                    </th>
                    <th scope='col' className='px-6 py-3 text-right text-xs font-medium uppercase tracking-wider'>
                      {t('Amount')}
                    </th>
                  </tr>
                </thead>
                <tbody className='divide-y divide-gray-200 dark:divide-gray-700'>
                  {/* Base Plan */}
                  <tr className='bg-gray-50 hover:bg-gray-100 dark:bg-gray-800 dark:hover:bg-gray-700'>
                    <td className='px-6 py-4 whitespace-nowrap text-sm font-medium'>
                      {invoice.subscription.basePlan.name}
                    </td>
                    <td className='px-6 py-4 whitespace-nowrap text-sm text-right'>
                      {invoice.subscription.basePlan.amount}
                    </td>
                    <td className='px-6 py-4 whitespace-nowrap text-sm text-right'>1</td>
                    <td className='px-6 py-4 whitespace-nowrap text-sm text-right'>
                      {invoice.subscription.basePlan.amount}
                    </td>
                  </tr>

                  {invoice.subscription.addons.map((addon: any, index: any) => (
                    <tr key={index} className='hover:bg-gray-50 dark:hover:bg-gray-700'>
                      <td className='px-6 py-4 whitespace-nowrap text-sm'>{addon.name}</td>
                      <td className='px-6 py-4 whitespace-nowrap text-sm text-right'>{addon.unitPrice}</td>
                      <td className='px-6 py-4 whitespace-nowrap text-sm text-right'>{addon.quantity}</td>
                      <td className='px-6 py-4 whitespace-nowrap text-sm text-right'>{addon.amount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className='flex justify-end'>
              <div className='w-64'>
                <div className='flex justify-between mb-2'>
                  <span className='text-gray-600 dark:text-gray-300'>{t('Subtotal')}</span>
                  <span>{invoice.amount}</span>
                </div>
                <div className='flex justify-between mb-2'>
                  <span className='text-gray-600 dark:text-gray-300'>{t('Tax')}</span>
                  <span>$0.00</span>
                </div>
                <div className='flex justify-between pt-2 border-t border-gray-300 dark:border-gray-600 font-semibold'>
                  <span>{t('Total')}</span>
                  <span>{invoice.amount}</span>
                </div>
              </div>
            </div>

            <div className='mt-12 pt-6 border-t border-gray-300 dark:border-gray-600'>
              <div className='flex flex-col md:flex-row justify-between'>
                <div className='mb-4 md:mb-0'>
                  <h4 className='text-sm font-semibold mb-2'>{t('Subscription Summary')}</h4>
                  <div className='text-sm text-gray-500 dark:text-gray-400'>
                    <p>{t('Your subscription includes')}:</p>
                    <ul className='list-disc pl-5 mt-1'>
                      <li>{invoice.subscription.basePlan.name}</li>
                      {invoice.subscription.addons.map((addon: any, index: number) => (
                        <li key={index}>
                          {addon.quantity}x {addon.name}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
                <div className='text-right'>
                  <p className='text-gray-500 dark:text-gray-400'>
                    {t('Payment processed with card ending in')} {invoice.cardLast4}
                  </p>
                  <p className='mt-2 text-gray-500 dark:text-gray-400'>{t('Thank you for your business!')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
